import { zodResolver } from '@hookform/resolvers/zod';
import { Replay } from '@mui/icons-material';
import { Button, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router';
import { DialogType } from '../../../@types';
import { Customer, Partner } from '../../../@types/api';
import {
  LS_KEYS,
  PAYMENT_METHOD_FILTER_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_TRANSACTION_TYPE_OPTIONS,
  QUERY_KEYS,
} from '../../../constants';
import { PAYMENT_GATEWAY } from '../../../constants/enums';
import { paymentReportFiltersSchema, type PaymentReportFilters } from '../../../schema/paymentReportFilters';
import { AdditionalQueryParams } from '../../../hooks/usePagination';
import { useMarketplace } from '../../../hooks/useMarketplace';
import usePersist from '../../../hooks/usePersist';
import FormSelect from '../../molecules/FormSelect';
import FormAutocomplete from '../FormAutocomplete';
import { getCompleteName, normalizePaymentMethodFilter } from '../../../utils';

export const styles = {
  display: 'flex',
  flexDirection: 'column-reverse',
  alignItems: 'center',
  gap: '2',
};

const defaultValues: PaymentReportFilters = {
  playerIds: [],
  partnerIds: [],
  type: undefined,
  status: undefined,
  paymentMethod: 'ALL',
  cryptoProvider: '',
  cryptoAsset: '',
  cryptoNetwork: '',
};

export type FormData = PaymentReportFilters;

const CRYPTO_PROVIDER_OPTIONS = [
  { id: '', name: 'All providers' },
  { id: 'zero_x_processing', name: '0xProcessing' },
  { id: 'moralis', name: 'Moralis' },
];

export const preparePaymentReportFilters = (data: FormData, hasCryptoPaymentGateway: boolean) => {
  const normalized = normalizePaymentMethodFilter(
    { ...data, paymentMethod: data.paymentMethod ?? undefined },
    hasCryptoPaymentGateway
  );
  const includeCrypto = hasCryptoPaymentGateway && data.paymentMethod !== 'Agent';
  return {
    ...normalized,
    cryptoProvider: includeCrypto ? data.cryptoProvider || undefined : undefined,
    cryptoAsset: includeCrypto ? data.cryptoAsset || undefined : undefined,
    cryptoNetwork: includeCrypto ? data.cryptoNetwork || undefined : undefined,
  };
};

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

const FilterPaymentReportsList = ({ changeQuery, closeModal }: Props) => {
  const { pathname } = useLocation();
  const { config, isStandaloneCasino } = useMarketplace();
  const page = pathname.split('/').pop();

  const { setPersistData, getPersistData, deletePersistData } = usePersist(false);

  const formDataKey = `${LS_KEYS.appFormFilter}-${pathname}`;
  const normalizeFormData = (data: FormData): FormData =>
    isStandaloneCasino
      ? {
          playerIds: data.playerIds ?? [],
          partnerIds: undefined,
          type: undefined,
          status: undefined,
          paymentMethod: undefined,
          cryptoProvider: undefined,
          cryptoAsset: undefined,
          cryptoNetwork: undefined,
        }
      : data;
  const getSavedData = () => normalizeFormData({ ...defaultValues, ...getPersistData<FormData>(formDataKey) });

  const { control, handleSubmit, reset } = useForm<FormData>({
    resolver: zodResolver(paymentReportFiltersSchema),
    defaultValues: getSavedData(),
  });

  const paymentMethod = useWatch({ control, name: 'paymentMethod' });
  const hasCryptoPaymentGateway = config?.marketplaceConfig?.paymentGateway === PAYMENT_GATEWAY.CRYPTO;

  const handleFilterReset = () => {
    deletePersistData(formDataKey);
    const data = normalizeFormData(defaultValues);
    reset(data);
    changeQuery(preparePaymentReportFilters(data, hasCryptoPaymentGateway));
    closeModal?.();
  };

  const onFormSubmit = (data: FormData) => {
    const normalized = normalizeFormData(data);
    setPersistData<FormData>(formDataKey, normalized);
    changeQuery(preparePaymentReportFilters(normalized, hasCryptoPaymentGateway));
    closeModal?.();
  };

  return (
    <>
      <DialogTitle variant='h4' gutterBottom>
        Filter {page}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <FormAutocomplete<Customer, FormData>
            name='playerIds'
            control={control}
            queryKey={[QUERY_KEYS.customers]}
            apiService='crm'
            url='customer/search'
            multiple
            getOptionLabel={(options, value) => {
              const option = options.find((option) => option.userId === value);
              return option ? `${getCompleteName(option.firstName, option.lastName, option.username)}` : '';
            }}
            label='Players'
            optionsFilter='userId'
          />
          {!isStandaloneCasino && (
            <>
              <FormAutocomplete<Partner, FormData>
                name='partnerIds'
                control={control}
                queryKey={[QUERY_KEYS.agentSearch]}
                apiService='crm'
                url='agent/search'
                label='Partner'
                multiple
                getOptionLabel={(options, value) => {
                  const option = options.find((option) => option.userId === value);
                  return option ? option.fullName : '';
                }}
                optionsFilter='userId'
              />
              {hasCryptoPaymentGateway && (
                <FormSelect
                  label='Payment method'
                  name='paymentMethod'
                  control={control}
                  options={PAYMENT_METHOD_FILTER_OPTIONS}
                />
              )}
              {hasCryptoPaymentGateway && paymentMethod !== 'Agent' && (
                <>
                  <FormSelect
                    label='Crypto provider'
                    name='cryptoProvider'
                    control={control}
                    options={CRYPTO_PROVIDER_OPTIONS}
                  />
                  <Controller
                    name='cryptoAsset'
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        label='Crypto currency/asset'
                        placeholder='USDT'
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message ??
                          'Exact asset symbol, e.g. USDT (all networks). Leave empty for all assets.'
                        }
                        slotProps={{ htmlInput: { maxLength: 32 } }}
                      />
                    )}
                  />
                  <Controller
                    name='cryptoNetwork'
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        label='Network'
                        placeholder='BEP20'
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message ??
                          'Exact network, e.g. BEP20, ERC20 or TRC20. Leave empty for all networks.'
                        }
                        slotProps={{ htmlInput: { maxLength: 32 } }}
                      />
                    )}
                  />
                </>
              )}
              <FormSelect label='Type' name='type' control={control} options={PAYMENT_TRANSACTION_TYPE_OPTIONS} />
              <FormSelect label='Status' name='status' control={control} options={PAYMENT_STATUS_OPTIONS} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant='text' onClick={handleFilterReset} startIcon={<Replay />} size='small'>
          Reset filters
        </Button>
        <Button variant='outlined' onClick={closeModal}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit(onFormSubmit)}>
          Save
        </Button>
      </DialogActions>
    </>
  );
};

export default FilterPaymentReportsList;
